<script lang="ts">
	import type { DatasheetPoiData } from '$lib/components/pois/types';
	import { page } from '$app/state';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import DatasheetLocation from '$lib/components/pois/datasheet/DatasheetLocation.svelte';
	import DatasheetDetailLocation from '$lib/components/pois/datasheet/detail/DatasheetDetailLocation.svelte';
	import DatasheetDetailPracticalInfos from '$lib/components/pois/datasheet/detail/DatasheetDetailPracticalInfos.svelte';
	import DatasheetDetailStrongPoints from '$lib/components/pois/datasheet/detail/DatasheetDetailStrongPoints.svelte';
	import DatasheetDetailText from '$lib/components/pois/datasheet/detail/DatasheetDetailText.svelte';
	import DatasheetEvConnectors from '$lib/components/pois/datasheet/detail/ev-connectors/DatasheetEVConnectors.svelte';
	import DatasheetToolbar from '$lib/components/pois/datasheet/toolbar/DatasheetToolbar.svelte';
	import { getBookUrl } from '$lib/components/pois/utils';
	import { isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';

	interface Props {
		poi: DatasheetPoiData;
		onSeeOnMap?: () => void;
		onItineraryClick?: () => void;
		onLearnMoreClick?: () => void;
	}

	const {
		poi,
		onSeeOnMap,
		onItineraryClick,
		onLearnMoreClick,
	}: Props = $props();
	const searchParams = getSearchParamsStore();
</script>

{#if poi.__typename === 'AccommodationPOI'}
	{#if poi.description}
		<DatasheetDetailText title={m.poi_datasheet__description()} text={poi.description} />
	{/if}
	{#if poi.facilities}
		<DatasheetDetailStrongPoints strongPoints={poi.facilities} color="hotelAlt" />
	{/if}
{:else if poi.__typename === 'AccommodationMichelinPOI'}
	{#if poi.description}
		<DatasheetDetailText title={m.poi_datasheet__description()} text={poi.description} />
	{/if}
	{#if poi.facilities}
		<DatasheetDetailStrongPoints strongPoints={poi.facilities} color="hotelAlt" />
	{/if}
{:else if poi.__typename === 'RestaurantMichelinPOI'}
	{#if poi.description}
		<DatasheetDetailText title={m.poi_datasheet__michelin_opinion()} text={poi.description} />
	{/if}
	{#if poi.facilities}
		<DatasheetDetailStrongPoints strongPoints={poi.facilities} color="restaurantAlt" />
	{/if}
{:else if poi.__typename === 'TourismPOI'}
	{#if poi.description}
		<DatasheetDetailText
			title={m.poi_datasheet__michelin_opinion__tourism()}
			text={poi.description}
		/>
	{/if}
	{#if poi.phone || poi.redirectUrl}
		<DatasheetDetailPracticalInfos
			phone={poi.phone ?? undefined}
			website={poi.redirectUrl ?? undefined}
		/>
	{/if}
{:else if poi.__typename === 'ServiceEVPOI'}
	{#if poi.connectors}
		<DatasheetEvConnectors connectors={poi.connectors} />
	{/if}
{:else if poi.__typename === 'ServiceRestStopPOI'}
	{#if poi.fullAddress}
		<div class="flex justify-center">
			<DatasheetLocation
				{onSeeOnMap}
				address={poi.fullAddress}
				onAddItineraryClick={() => {
					onItineraryClick?.();
				}}
			/>
		</div>
	{/if}
{:else if isAnyPoiPartner(poi)}
	<DatasheetToolbar
		showItineraryButton
		showLearnMoreButton
		hasShadow={false}
		bookUrl={getBookUrl(poi, page, $searchParams)}
		learnMoreLabel={poi.callToAction}
		{onItineraryClick}
		{onLearnMoreClick}
	/>

	{#if poi.description}
		<DatasheetDetailText title={poi.datasheetTitle} text={poi.description} />
	{/if}
	{#if poi.practicalInformations || poi.practicalInformations2 || poi.practicalInformations3}
		<DatasheetDetailPracticalInfos
			infos={[poi.practicalInformations, poi.practicalInformations2, poi.practicalInformations3]}
		/>
	{/if}
{/if}
{#if poi.address.formattedAddress || poi.address.formattedCity}
	<DatasheetDetailLocation
		streetName={poi.address.formattedAddress || undefined}
		city={poi.address.formattedCity || undefined}
	/>
{/if}

{#if poi.__typename !== 'ServiceRestStopPOI'}
	<Button
		color="primary"
		icon="map"
		onclick={() => { onSeeOnMap?.(); }}
		text={m.poi_datasheet__see_on_map()}
		variant="outline"
	/>
{/if}
